import { useCallback, useMemo, useState } from 'react';

import debounce from 'bloko/common/debounce';

import { filterDistrictItems } from 'src/components/Search/Resume/Filters/Geo/Filters/District/utils/filter';
import { useSelector } from 'src/hooks/useSelector';
import { DistrictItem } from 'src/types/search/common';

import { useGeoFiltersAnalytics } from 'src/components/Search/Resume/Filters/Geo/hooks/useAnalytics';

const FILTER_DEBOUNCE = 300;
const SEARCH_QUERY_LIMIT_LOW = 2;

export const useSearchDistrictState = (selectedAreaId: number) => {
    const [isAnalyticsSent, setAnalyticsSent] = useState(false);
    const { sendEventSearchDistricts } = useGeoFiltersAnalytics();

    const districts = useSelector((state) => state.citiesDistricts[selectedAreaId]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchItems, setSearchItems] = useState<Array<DistrictItem>>(filterDistrictItems(searchQuery, districts));

    const debouncedSearch = useMemo(
        () =>
            debounce((query: string, districts: DistrictItem[]) => {
                if (query.length >= SEARCH_QUERY_LIMIT_LOW) {
                    const searchedItems = filterDistrictItems(query, districts);
                    setSearchItems(searchedItems);

                    if (!isAnalyticsSent) {
                        setAnalyticsSent(true);
                        sendEventSearchDistricts();
                    }
                } else if (!query) {
                    setSearchItems(districts);

                    if (isAnalyticsSent) {
                        setAnalyticsSent(false);
                    }
                }
            }, FILTER_DEBOUNCE),
        [isAnalyticsSent, sendEventSearchDistricts]
    );

    const handleQueryChange = useCallback(
        (query: string) => {
            setSearchQuery(query);
            debouncedSearch(query, districts);
        },
        [debouncedSearch, districts]
    );

    return useMemo(
        () => ({
            query: searchQuery,
            searchItems,
            handleQueryChange,
        }),
        [searchQuery, searchItems, handleQueryChange]
    );
};
